import { DT } from "../../constants.ts";
import { add, flatBasis, norm, scale, sub } from "../../math.ts";
import { Btn } from "../../net/input.ts";
import { ActionKind, hv, setHv } from "../../sim/components.ts";
import { aim, applyDamage, applyStatus, damageBarrier, isEnemy, playersInRadius, spawnZone, type DamageInfo } from "../../sim/combat.ts";
import { FxKind } from "../../sim/events.ts";
import { HeroId, Role, Slot, type HeroDef } from "../types.ts";
import { fxk, pk, zk, type HeroModule } from "../module.ts";
import { action, commonInput, consume, flight, launchProjectile, ownerAlive, ready, simpleProjectile, spreadDir, toGround, trigger, useUlt } from "../kitlib.ts";

export const V = { barrierUp: 0, barrierHp: 1, barrierRegenDelay: 2, darkMagic: 3, levitating: 4, levitateTicks: 5 } as const;
export const PK = { Dagger: pk(HeroId.DoctorEstranged, 0) } as const;
export const ZK = { Portal: zk(HeroId.DoctorEstranged, 0) } as const;
export const FX = { Maelstrom: fxk(HeroId.DoctorEstranged, 0), SoulSever: fxk(HeroId.DoctorEstranged, 1) } as const;
export const DOCTOR_BARRIER = { upVar: V.barrierUp, hpVar: V.barrierHp, maxHp: 850, width: 5, height: 3, forward: 1.6, regen: 60, regenDelay: 60 } as const;

const def: HeroDef = {
  id: HeroId.DoctorEstranged,
  slug: "doctor-estranged",
  name: "Doctor Estranged",
  role: Role.Tank,
  color: "#c9a14a",
  blurb: "Warding sorcerer with an 850-HP shield, free-flight cloak, paired portals and damage-fed dark magic.",
  health: 650,
  armor: 0,
  shields: 0,
  radius: 0.6,
  height: 2.2,
  headRadius: 0.28,
  speed: 6,
  jumpSpeed: 7.45,
  ammo: 12,
  reloadTime: 0,
  ultCost: 3100,
  abilities: {
    [Slot.Primary]: { name: "Denial Daggers", key: "LMB", description: "Throw five mystic blades that each deal 18 damage and build dark magic.", cooldown: 0 },
    [Slot.Secondary]: { name: "Seraphic Ward", key: "RMB (hold)", description: "Project an 850-HP frontal shield that recovers after a short delay.", cooldown: 0 },
    [Slot.Ability1]: { name: "Cloak Drift", key: "Shift", description: "Lift into controlled flight for six seconds.", cooldown: 12 },
    [Slot.Ability2]: { name: "Madness Spiral", key: "E", description: "Release 1.3x stored dark magic around you; full magic prevents healing for 2 seconds.", cooldown: 6 },
    [Slot.Ability3]: { name: "Far-Away Gate", key: "F", description: "Open a two-way portal pair for 20 seconds.", cooldown: 180 },
    [Slot.Ultimate]: { name: "Soul Sundering", key: "Q", description: "Separate nearby enemy spirits, stunning them and echoing linked damage.", cooldown: 0 },
  },
  passive: { name: "Cost of Conjuring", description: "Weapon hits build dark magic, up to 100." },
};

const module: HeroModule = {
  def,
  barrier: DOCTOR_BARRIER,
  kit: {
    init(sim, eid) {
      setHv(sim.c, eid, V.barrierHp, DOCTOR_BARRIER.maxHp);
    },
    tick(sim, eid, input, pressed, canAct) {
      const c = sim.c;
      const delay = hv(c, eid, V.barrierRegenDelay);
      if (delay > 0) setHv(c, eid, V.barrierRegenDelay, delay - 1);
      else if (!hv(c, eid, V.barrierUp)) setHv(c, eid, V.barrierHp, Math.min(DOCTOR_BARRIER.maxHp, hv(c, eid, V.barrierHp) + DOCTOR_BARRIER.regen * DT));

      commonInput(sim, eid, input, pressed);
      const barrierHeld = canAct && !!(input.buttons & Btn.Secondary) && hv(c, eid, V.barrierHp) > 0 && hv(c, eid, V.barrierRegenDelay) < 250;
      setHv(c, eid, V.barrierUp, barrierHeld ? 1 : 0);
      if (!canAct) return;

      if (useUlt(sim, eid, input, pressed)) {
        const center = add(sim.posOf(eid), { x: 0, y: 1, z: 0 });
        sim.emit({ type: "explosion", pos: center, radius: 10, kind: FX.SoulSever });
        for (const { eid: t } of playersInRadius(sim, center, 10, (e) => isEnemy(sim, eid, e))) {
          applyStatus(sim, t, "stun", 3, { source: eid });
          applyStatus(sim, t, "linked", 3, { source: eid });
        }
      }

      if ((pressed & Btn.Ability1) && ready(sim, eid, Slot.Ability1)) {
        consume(sim, eid, Slot.Ability1);
        setHv(c, eid, V.levitateTicks, 360);
        action(sim, eid, ActionKind.Cast, 14);
      }

      if ((pressed & Btn.Ability3) && ready(sim, eid, Slot.Ability3)) {
        const aimPoint = aim(sim, eid, 40, false);
        const far = Math.hypot(aimPoint.point.x - aimPoint.origin.x, aimPoint.point.z - aimPoint.origin.z);
        if (far >= 6) {
          consume(sim, eid, Slot.Ability3);
          action(sim, eid, ActionKind.Cast, 20);
          if (sim.authority) {
            const { fwd } = flatBasis(input.yaw);
            const here = toGround(sim, add(sim.posOf(eid), { x: fwd.x * 2, y: 1, z: fwd.z * 2 }));
            const back = norm({ x: aimPoint.point.x - aimPoint.origin.x, y: 0, z: aimPoint.point.z - aimPoint.origin.z });
            const there = toGround(sim, sub(aimPoint.point, scale(back, 1.2)));
            const za = spawnZone(sim, eid, ZK.Portal, here, 1.3, 20)!;
            const zb = spawnZone(sim, eid, ZK.Portal, there, 1.3, 20)!;
            c.Zone.link[za] = zb + 1;
            c.Zone.link[zb] = za + 1;
          }
        }
      }

      if ((pressed & Btn.Ability2) && ready(sim, eid, Slot.Ability2)) {
        consume(sim, eid, Slot.Ability2);
        action(sim, eid, ActionKind.Cast, 20);
        const dm = hv(c, eid, V.darkMagic);
        const center = add(sim.posOf(eid), { x: 0, y: 0.9, z: 0 });
        sim.emit({ type: "explosion", pos: center, radius: 8, kind: FX.Maelstrom });
        for (const { eid: t } of playersInRadius(sim, center, 8, (e) => isEnemy(sim, eid, e))) {
          applyDamage(sim, t, dm * 1.3, eid);
          if (dm >= 100) applyStatus(sim, t, "antiHeal", 2, { source: eid });
        }
        setHv(c, eid, V.darkMagic, 0);
      }

      if (!barrierHeld && trigger(sim, eid, !!(input.buttons & Btn.Primary), true)) {
        c.Weapon.fireCd[eid] = 48;
        action(sim, eid, ActionKind.Fire, 10);
        const a = aim(sim, eid);
        for (let i = 0; i < 5; i++) launchProjectile(sim, eid, PK.Dagger, 80, input, { radius: 0.1, seconds: 0.55, dir: spreadDir(a.dir, 0.055, c.Weapon.shots[eid]! * 17 + i * 5 + 3) });
      }
    },
    movement(sim, eid, input, mods) {
      if (hv(sim.c, eid, V.barrierUp)) mods.speedMul *= 0.7;
      const ticks = hv(sim.c, eid, V.levitateTicks);
      setHv(sim.c, eid, V.levitating, ticks > 0 ? 1 : 0);
      if (ticks > 0) {
        setHv(sim.c, eid, V.levitateTicks, ticks - 1);
        flight(sim, eid, input, mods, { speed: 6, vertical: 4.5, accel: 28, pitchClimb: true });
      }
    },
    onDealtDamage(sim, eid, _target, amount, _info: DamageInfo) {
      setHv(sim.c, eid, V.darkMagic, Math.min(100, hv(sim.c, eid, V.darkMagic) + amount * (3.5 / 18)));
    },
  },
  projectiles: {
    [PK.Dagger]: simpleProjectile(
      (sim, _p, owner, hit) => {
        if (!ownerAlive(sim, owner)) return;
        if (hit.target !== undefined) applyDamage(sim, hit.target, 18, owner, { pos: hit.pos });
        else if (hit.barrier !== undefined) damageBarrier(sim, hit.barrier, 18, owner);
        else sim.emit({ type: "impact", pos: hit.pos, kind: FxKind.Pellet });
      },
      { color: "#ffb347", size: 0.14, trail: true },
    ),
  },
  fx: {
    [FX.Maelstrom]: { color: "#8a6cff" },
    [FX.SoulSever]: { color: "#58d7ff" },
  },
  zones: {
    [ZK.Portal]: {
      visual: { color: "#ff9a3c", dome: false },
      tick(sim, z) {
        const c = sim.c;
        const partner = c.Zone.link[z]! - 1;
        if (partner < 0 || c.Zone.kind[partner] !== ZK.Portal || c.Zone.link[partner]! - 1 !== z) return;
        const from = sim.posOf(z);
        const to = sim.posOf(partner);
        for (const e of sim.playerEids()) {
          if (!c.Player.alive[e] || (sim.teleportCd.get(e) ?? 0) > sim.tick) continue;
          const p = sim.posOf(e);
          if (Math.hypot(p.x - from.x, p.z - from.z) > c.Zone.radius[z]! || Math.abs(p.y - from.y) > 2) continue;
          c.Pos.x[e] = to.x;
          c.Pos.y[e] = to.y + 0.05;
          c.Pos.z[e] = to.z;
          sim.teleportCd.set(e, sim.tick + 90);
          sim.emit({ type: "impact", pos: to, kind: FxKind.Singularity });
        }
      },
    },
  },
};

export default module;
